import { createTheme } from '@mui/material/styles';
import { COLORS } from '../../theme';

// Tema exclusivo da área do cliente: creme/grafite/laranja, cartões claros,
// tipografia Poppins nos títulos e botões em pílula — alinhado à marca.
const getTokens = (mode) => {
  const isLight = mode === 'light';
  const background = isLight ? COLORS.cream : COLORS.dark;
  const paper = isLight ? COLORS.white : COLORS.darkSoft;
  const elevated = isLight ? COLORS.creamSoft : COLORS.darkElevated;
  const textPrimary = isLight ? COLORS.dark : COLORS.cream;
  const textSecondary = isLight ? 'rgba(28,27,25,0.66)' : 'rgba(242,238,231,0.7)';
  const divider = isLight ? COLORS.line : 'rgba(242,238,231,0.14)';
  const cardShadow = isLight ? '0 12px 30px rgba(28,27,25,0.07)' : '0 14px 32px rgba(0,0,0,0.28)';

  return {
    palette: {
      mode,
      primary: {
        main: COLORS.orange,
        light: COLORS.orangeLight,
        dark: COLORS.orangeDeep,
        contrastText: COLORS.white,
      },
      secondary: {
        main: isLight ? COLORS.dark : COLORS.cream,
        contrastText: isLight ? COLORS.cream : COLORS.dark,
      },
      background: { default: background, paper, elevated },
      text: { primary: textPrimary, secondary: textSecondary },
      divider,
      success: { main: COLORS.successDark },
      warning: { main: COLORS.warningDark },
      info: { main: COLORS.infoDark },
      error: { main: COLORS.errorDark },
      status: {
        aberto: { main: COLORS.warning, bg: COLORS.statusOpenBg },
        emAndamento: { main: COLORS.info, bg: COLORS.infoSoft },
        concluido: { main: COLORS.success, bg: COLORS.statusDoneBg },
        cancelado: { main: COLORS.error, bg: COLORS.statusCancelledBg },
        pendente: { main: COLORS.warning },
        pago: { main: COLORS.success },
        pagoComSaldo: { main: COLORS.success },
        pagoParcialSaldo: { main: COLORS.info },
      },
      whatsapp: { main: COLORS.whatsapp, hover: COLORS.whatsappDark },
      green: { main: COLORS.successDark },
      alert: { main: COLORS.alert },
      chart: {
        primary: COLORS.successDark,
        secondary: COLORS.infoDark,
        tertiary: COLORS.chartPurple,
        quaternary: COLORS.errorDeep,
        quinary: COLORS.warningDark,
        senary: COLORS.cyan,
        septenary: COLORS.purple,
        octonary: COLORS.pink,
        nonary: COLORS.indigo,
        denary: COLORS.redLight,
        undenary: COLORS.brown,
        duodenary: COLORS.blueGray,
      },
      gradients: {
        primary: `linear-gradient(135deg, ${COLORS.orange} 0%, ${COLORS.orangeLight} 100%)`,
      },
    },
    shape: { borderRadius: 14 },
    typography: {
      fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, sans-serif',
      h1: { fontFamily: '"Poppins", sans-serif', fontWeight: 900, letterSpacing: '-0.03em' },
      h2: { fontFamily: '"Poppins", sans-serif', fontWeight: 900, letterSpacing: '-0.02em' },
      h3: { fontFamily: '"Poppins", sans-serif', fontWeight: 800, letterSpacing: '-0.02em' },
      h4: { fontFamily: '"Poppins", sans-serif', fontWeight: 800, letterSpacing: '-0.02em' },
      h5: { fontFamily: '"Poppins", sans-serif', fontWeight: 800, letterSpacing: '-0.01em' },
      h6: { fontFamily: '"Poppins", sans-serif', fontWeight: 700 },
      button: { textTransform: 'none', fontWeight: 700, letterSpacing: 0 },
    },
    components: {
      MuiCard: {
        styleOverrides: {
          root: {
            borderRadius: 20,
            border: `1px solid ${divider}`,
            boxShadow: cardShadow,
            backgroundImage: 'none',
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: { backgroundImage: 'none', borderRadius: 16 },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: 50,
            padding: '8px 18px',
            fontWeight: 700,
            textTransform: 'none',
          },
          contained: { boxShadow: 'none' },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: { borderRadius: 50, fontWeight: 600 },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: { borderRadius: 12 },
        },
      },
      MuiAccordion: {
        styleOverrides: {
          root: {
            borderRadius: 16,
            border: `1px solid ${divider}`,
            boxShadow: 'none',
            '&:before': { display: 'none' },
            '&.Mui-expanded': { margin: 0 },
            marginBottom: 12,
          },
        },
      },
      MuiDialog: {
        styleOverrides: {
          paper: { borderRadius: 24, backgroundImage: 'none' },
        },
      },
      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            backgroundColor: COLORS.dark,
            color: COLORS.cream,
            fontSize: '0.78rem',
            lineHeight: 1.5,
            padding: '8px 12px',
            borderRadius: 10,
            border: '1px solid rgba(242,238,231,0.16)',
          },
          arrow: { color: COLORS.dark },
        },
      },
      MuiTabs: {
        styleOverrides: {
          indicator: { borderRadius: 4, height: 3 },
        },
      },
      MuiListItemButton: {
        styleOverrides: {
          root: { borderRadius: 12 },
        },
      },
    },
  };
};

const userLightTheme = createTheme(getTokens('light'));
const userDarkTheme = createTheme(getTokens('dark'));

export { userLightTheme, userDarkTheme };
